import dayjs, { Dayjs } from 'dayjs';

// Check if two dates are the same day
export const isSameDay = (
  date1: Dayjs | undefined | null,
  date2: Dayjs | undefined | null
): boolean => !!date1 && !!date2 && date1.isSame(date2, 'day');

// Check if date is within selected range
export const isInRange = (
  date: Dayjs,
  startDate: Dayjs | undefined | null,
  endDate: Dayjs | undefined | null
): boolean => {
  return (
    !!startDate &&
    !!endDate &&
    date.isSameOrAfter(startDate, 'day') &&
    date.isSameOrBefore(endDate, 'day')
  );
};

export const isToday = (date: Dayjs): boolean => date.isSame(dayjs(), 'day');

export enum DatePickerQuickType {
  Today = 'today',
  ThisWeek = 'thisWeek',
  LastWeek = 'lastWeek',
  ThisMonth = 'thisMonth',
  Last7Day = 'last7Day',
  Last14Day = 'last14Day',
  Last30Day = 'last30Day',
  Last45Day = 'last45Day',
  Last60Day = 'last60Day',
  Custom = 'custom',
}

export const SIZE_MAP: Record<'small' | 'middle' | 'large', string> = {
  small: '22px',
  middle: '32px',
  large: '40px',
};

// Quick filter options for date ranges
export const QUICK_FILTERS = [
  { label: 'Today', key: DatePickerQuickType.Today },
  { label: 'This Week', key: DatePickerQuickType.ThisWeek },
  { label: 'Last Week', key: DatePickerQuickType.LastWeek },
  { label: 'This Month', key: DatePickerQuickType.ThisMonth },
  { label: 'Last 7 days', key: DatePickerQuickType.Last7Day },
  { label: 'Last 14 days', key: DatePickerQuickType.Last14Day },
  { label: 'Last 30 days', key: DatePickerQuickType.Last30Day },
  { label: 'Last 45 days', key: DatePickerQuickType.Last45Day },
  { label: 'Last 60 days', key: DatePickerQuickType.Last60Day },
  { label: 'Custom', key: DatePickerQuickType.Custom },
];

export const getRangeDateFromType = (type: string) => {
  const today = dayjs();
  const quickFilterMap: Record<string, () => [Dayjs, Dayjs]> = {
    [DatePickerQuickType.Today]: () => [
      today.startOf('day'),
      today.endOf('day'),
    ],
    [DatePickerQuickType.ThisWeek]: () => [
      today.startOf('week'),
      today.endOf('week'),
    ],
    [DatePickerQuickType.LastWeek]: () => [
      today.subtract(1, 'week').startOf('week'),
      today.subtract(1, 'week').endOf('week'),
    ],
    [DatePickerQuickType.ThisMonth]: () => [
      today.startOf('month'),
      today.endOf('month'),
    ],
    [DatePickerQuickType.Last7Day]: () => [today.subtract(7, 'days'), today],
    [DatePickerQuickType.Last14Day]: () => [today.subtract(14, 'days'), today],
    [DatePickerQuickType.Last30Day]: () => [today.subtract(30, 'days'), today],
    [DatePickerQuickType.Last45Day]: () => [today.subtract(45, 'days'), today],
    [DatePickerQuickType.Last60Day]: () => [today.subtract(60, 'days'), today],
  };

  const [start, end] = quickFilterMap[type]?.() ?? [undefined, undefined];
  return { start, end, today };
};

// Generate days for calendar view
export const generateCalendarDays = (date: Dayjs): Dayjs[] =>
  Array.from({ length: date.daysInMonth() }, (_, i) =>
    date.startOf('month').add(i, 'day')
  );
